import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, RotateCw, ArrowUp, ArrowDown, Save, Sparkles, Loader2, ImageIcon, AlertCircle, Maximize2, Palette, Smile, Type, Frame, CheckCircle, Download, Package, Menu, ArrowLeft, Share2, Undo2, Redo2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import NativeBridge, { isNativeApp, canPurchaseDigitalGoods } from './native-bridge';
import { VisionBoardGenerator } from './components/generator';
import { api } from './services/api';
import { REFERENCE_WIDTH } from './utils/constants';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toast, OfflineBanner, Modal } from './components/common';
import { PrintModal, ShareModal } from './components/modals';
import Dashboard from './pages/Dashboard';
import LoginScreen from './pages/LoginScreen';
import SharedBoardView from './pages/SharedBoardView';
import AppRedirect from './pages/AppRedirect';
import { PaymentSuccessWeb, PaymentSuccessMobile, PaymentSuccessIOS } from './pages/PaymentSuccess';
import Support from './pages/Support';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// Main App Component with URL-based routing
const VisionBoardApp = () => {
  const { user, token, logout, isLoading, fetchUserStats, handleBuyCredits } = useAuth();

  // Handle non-hash URLs by converting them to hash URLs
  // This ensures URLs like /shared/TOKEN work by redirecting to /#/shared/TOKEN
  const getInitialRoute = () => {
    // If there's already a hash, use it
    if (window.location.hash) {
      return window.location.hash.slice(1) || '/';
    }
    // If pathname has a route (not just /), convert to hash routing
    const pathname = window.location.pathname;
    const search = window.location.search;
    if (pathname && pathname !== '/') {
      const nextRoute = `${pathname}${search}`;
      // Redirect to hash-based URL
      window.location.replace(`${window.location.origin}/#${nextRoute}`);
      return nextRoute;
    }
    return '/';
  };

  const [route, setRoute] = useState(getInitialRoute);
  const [boards, setBoards] = useState({});

  // Listen to hash changes (keep full hash including query params)
  useEffect(() => {
    const handleHashChange = () => {
      const newHash = window.location.hash.slice(1) || '/';
      setRoute(newHash);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigateTo = (path) => {
    window.location.hash = path;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Parse route early to check for shared boards (public access)
  const routeWithoutQuery = route.split('?')[0];
  const pathParts = routeWithoutQuery.split('/').filter(Boolean);
  const [page, ...params] = pathParts;

  // Handle shared board view (public - no auth required)
  if (page === 'shared' && params[0]) {
    return <SharedBoardView shareToken={params[0]} onNavigate={navigateTo} />;
  }

  if (page === 'support') {
    return <Support onNavigate={navigateTo} token={token} />;
  }
  if (page === 'privacy') {
    return <Privacy onNavigate={navigateTo} />;
  }
  if (page === 'terms') {
    return <Terms onNavigate={navigateTo} />;
  }

  // Handle payment success pages - needs to be before auth check
  // because user might not be fully authenticated yet after Stripe redirect
  if (page === 'success-web') {
    return <PaymentSuccessWeb onNavigate={navigateTo} />;
  }
  if (page === 'success-mobile') {
    return <PaymentSuccessMobile onNavigate={navigateTo} />;
  }
  if (page === 'success-ios') {
    return <PaymentSuccessIOS onNavigate={navigateTo} />;
  }
  if (page === 'success') {
    return <PaymentSuccessWeb onNavigate={navigateTo} />;
  }

  // Handle payment redirect back to the native app (no auth required)
  if (page === 'app-redirect') {
    return <AppRedirect route={route} onNavigate={navigateTo} />;
  }

  if (!user && !token) {
    return <LoginScreen onNavigate={navigateTo} />;
  }

  // Parse route: /, /board/:id, /board/:id/add-vision, /board/new
  // (routeWithoutQuery, pathParts, page, params already defined above)

  if (page === 'board' && params[0]) {
    const boardId = params[0];
    const subpage = params[1]; // 'add-vision' or undefined

    // Handle creating a new board
    if (boardId === 'new') {
      const newBoardId = `board_${Date.now()}`;
      return (
        <VisionBoardGenerator
          boardId={newBoardId}
          boards={boards}
          setBoards={setBoards}
          onSave={() => navigateTo(`/board/${newBoardId}`)}
          onBack={() => navigateTo('/')}
          onNavigate={navigateTo}
          token={token}
          fetchUserStats={fetchUserStats}
          handleBuyCredits={handleBuyCredits}
        />
      );
    }

    if (subpage === 'add-vision') {
      return (
        <VisionBoardGenerator
          board={boards[boardId]}
          boardId={boardId}
          boards={boards}
          setBoards={setBoards}
          onSave={() => navigateTo(`/board/${boardId}`)}
          onBack={() => navigateTo('/board/' + boardId)}
          onNavigate={navigateTo}
          token={token}
          fetchUserStats={fetchUserStats}
          handleBuyCredits={handleBuyCredits}
        />
      );
    }

    // Board view (canvas)
    return (
      <BoardView
        boardId={boardId}
        boards={boards}
        setBoards={setBoards}
        onAddVision={() => navigateTo(`/board/${boardId}/add-vision`)}
        onBackToDashboard={() => navigateTo('/')}
        onNavigate={navigateTo}
      />
    );
  }

  // Default: Dashboard
  return (
    <Dashboard
      boards={boards}
      setBoards={setBoards}
      onCreateNew={() => navigateTo('/board/new')}
      onEditBoard={(boardId) => navigateTo(`/board/${boardId}`)}
      onLogout={() => {
        logout();
        navigateTo('/');
      }}
      onNavigate={navigateTo}
    />
  );
};

// Board View Component (Canvas Editor)
const BoardView = ({ boardId, boards, setBoards, onAddVision, onBackToDashboard, onNavigate }) => {
  const { token, handleOrderPrint, shareBoard, unshareBoard } = useAuth();
  const { t } = useTranslation();
  const board = boards[boardId];
  const [isLoading, setIsLoading] = useState(!board);
  const [loadError, setLoadError] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const [background, setBackground] = useState(board?.parsedData?.background || { type: 'gradient', value: 'from-gray-50 to-gray-100' });
  const [canvasImages, setCanvasImages] = useState(board?.parsedData?.canvasImages || []);
  const [canvasElements, setCanvasElements] = useState(board?.parsedData?.canvasElements || []);
  const [canvasFrame, setCanvasFrame] = useState(board?.parsedData?.canvasFrame || null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [canvasSize, setCanvasSize] = useState(board?.parsedData?.canvasSize || { key: 'a4', width: 210, height: 297, unit: 'mm' });
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const [mobileToolTab, setMobileToolTab] = useState(null); // null, 'element', 'background', 'emoji', 'text', 'frame', 'size', 'actions'
  const [scaleFactor, setScaleFactor] = useState(1);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState(board?.share_url || '');
  const [isSharing, setIsSharing] = useState(false);
  const [isPublic, setIsPublic] = useState(board?.is_public || false);
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingTextId, setEditingTextId] = useState(null);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [snapGuides, setSnapGuides] = useState({ v: false, h: false });
  // Bumped whenever the history stacks change so undo/redo buttons re-render
  const [, setHistoryVersion] = useState(0);
  const refreshingCanvasImagesRef = useRef(new Set());
  const failedRefreshAttemptsRef = useRef(new Set());
  const historyRef = useRef({ past: [], future: [] });
  const lastNudgeRecordRef = useRef(0);
  const lastTapRef = useRef({ id: null, time: 0 });
  // One history entry per pointer interaction (drag/pinch/handle-resize),
  // recorded on the first actual movement so tap-to-select stays out of undo
  const interactionRecordedRef = useRef(false);
  // Drag state kept in refs so pointer-move can update at most once per
  // animation frame and only touch the array the dragged item lives in.
  const dragTargetRef = useRef(null); // { id, kind, width, height }
  const pendingDragPosRef = useRef(null);
  const dragRafRef = useRef(null);
  // Two-finger pinch/rotate gesture on the selected item
  const gestureRef = useRef(null); // { id, kind, startDist, startAngle, base }
  const pendingGestureRef = useRef(null);
  const gestureRafRef = useRef(null);
  // Corner-handle resize session
  const resizeSessionRef = useRef(null); // { id, kind, base, originX, originY, startDist }
  const pendingResizeRef = useRef(null);
  const resizeRafRef = useRef(null);

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const getViewportSize = () => {
    const viewport = window.visualViewport;
    return {
      width: viewport?.width || window.innerWidth,
      height: viewport?.height || window.innerHeight
    };
  };

  const loadBoard = async () => {
    if (board) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(false);
    try {
      const data = await api.getBoard(token, boardId);
      const loadedBoard = {
        ...data.board,
        parsedData: JSON.parse(data.board.data || '{}')
      };

      // Update boards state
      setBoards(prev => ({
        ...prev,
        [boardId]: loadedBoard
      }));

      // Update local state
      setCanvasImages(loadedBoard.parsedData.canvasImages || []);
      setBackground(loadedBoard.parsedData.background || { type: 'gradient', value: 'from-gray-50 to-gray-100' });
      setCanvasElements(loadedBoard.parsedData.canvasElements || []);
      setCanvasFrame(loadedBoard.parsedData.canvasFrame || null);
      setCanvasSize(loadedBoard.parsedData.canvasSize || { key: 'a4', width: 210, height: 297, unit: 'mm' });
    } catch (error) {
      console.error('Error loading board:', error);
      setLoadError(true);
    }
    setIsLoading(false);
  };

  // Load board if not in state (e.g., on page refresh)
  useEffect(() => {
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, board, token]);

  useEffect(() => {
    const handleViewportChange = () => {
      setViewportSize(getViewportSize());
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    window.visualViewport?.addEventListener('resize', handleViewportChange);

    handleViewportChange();

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  // Cancel any queued animation frames on unmount
  useEffect(() => {
    return () => {
      [dragRafRef, gestureRafRef, resizeRafRef].forEach((ref) => {
        if (ref.current !== null) cancelAnimationFrame(ref.current);
      });
    };
  }, []);

  // --- Undo/redo history (bounded, in-memory) ---

  const HISTORY_LIMIT = 50;

  const captureSnapshot = () => ({
    canvasImages,
    canvasElements,
    background,
    canvasFrame,
    canvasSize,
  });

  // Call once at the START of a mutation (or gesture) so undo restores the
  // state from just before it.
  const recordHistory = () => {
    const { past } = historyRef.current;
    past.push(captureSnapshot());
    if (past.length > HISTORY_LIMIT) past.shift();
    historyRef.current.future = [];
    setHistoryVersion((v) => v + 1);
  };

  const applySnapshot = (snap) => {
    setCanvasImages(snap.canvasImages);
    setCanvasElements(snap.canvasElements);
    setBackground(snap.background);
    setCanvasFrame(snap.canvasFrame);
    setCanvasSize(snap.canvasSize);
    setSelectedImage(null);
    setEditingTextId(null);
    setHasUnsavedChanges(true);
  };

  const undo = () => {
    const { past, future } = historyRef.current;
    if (past.length === 0) return;
    future.push(captureSnapshot());
    applySnapshot(past.pop());
    setHistoryVersion((v) => v + 1);
  };

  const redo = () => {
    const { past, future } = historyRef.current;
    if (future.length === 0) return;
    past.push(captureSnapshot());
    applySnapshot(future.pop());
    setHistoryVersion((v) => v + 1);
  };

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;

  // Cmd/Ctrl+Z and Shift+Cmd/Ctrl+Z work anywhere in the editor except
  // while typing in an input
  useEffect(() => {
    const onKeyDown = (e) => {
      const el = e.target;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const canvasAspectRatio = canvasSize.width / canvasSize.height;
  const verticalPadding = viewportSize.width < 640 ? 120 : 140;
  const horizontalPadding = viewportSize.width < 640 ? 24 : 32;
  const usableHeight = Math.max(viewportSize.height - verticalPadding, 200);
  const usableWidth = Math.max(viewportSize.width - horizontalPadding, 200);
  const canvasDisplayWidth = Math.min(REFERENCE_WIDTH, usableWidth, usableHeight * canvasAspectRatio);
  const canvasDisplayHeight = canvasDisplayWidth / canvasAspectRatio;

  // Update scale factor when canvas resizes
  useEffect(() => {
    setScaleFactor(canvasDisplayWidth / REFERENCE_WIDTH);
  }, [canvasDisplayWidth]);

  const markImageFailed = (id) => {
    if (!id) return;
    setFailedImages(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const refreshCanvasImageUrl = async (image) => {
    if (!image?.filename || !token) {
      markImageFailed(image?.id);
      return;
    }

    const refreshKey = `${boardId}:${image.id}`;
    if (refreshingCanvasImagesRef.current.has(refreshKey)) {
      return;
    }

    refreshingCanvasImagesRef.current.add(refreshKey);
    try {
      const data = await api.getImageUrl(token, image.filename);
      if (!data?.url) {
        markImageFailed(image.id);
      }
      if (data?.url) {
        setCanvasImages(prev =>
          prev.map(img => (img.id === image.id ? { ...img, imageUrl: data.url, expiresAt: data.expiresAt } : img))
        );
        setBoards(prev => {
          const currentBoard = prev[boardId];
          if (!currentBoard) return prev;
          const parsedData = currentBoard.parsedData || {};
          const updatedImages = (parsedData.canvasImages || []).map(img =>
            img.id === image.id ? { ...img, imageUrl: data.url, expiresAt: data.expiresAt } : img
          );
          return {
            ...prev,
            [boardId]: {
              ...currentBoard,
              parsedData: { ...parsedData, canvasImages: updatedImages },
            }
          };
        });
      }
    } catch (error) {
      console.error('Failed to refresh canvas image URL:', error);
      markImageFailed(image.id);
    } finally {
      refreshingCanvasImagesRef.current.delete(refreshKey);
    }
  };

  // --- Direct manipulation: drag, pinch/rotate, corner-handle resize ---
  // All pointer-move work is throttled to one state update per animation
  // frame and only touches the array the manipulated item lives in.

  const SNAP_THRESHOLD = 8; // reference-px within which the item snaps to canvas center
  const CANVAS_KEEP_VISIBLE = 40; // reference-px of the item that must stay on canvas

  const referenceHeight = () => REFERENCE_WIDTH / canvasAspectRatio;

  const clampPosition = (x, y, width, height) => ({
    x: Math.min(Math.max(x, CANVAS_KEEP_VISIBLE - width), REFERENCE_WIDTH - CANVAS_KEEP_VISIBLE),
    y: Math.min(Math.max(y, CANVAS_KEEP_VISIBLE - height), referenceHeight() - CANVAS_KEEP_VISIBLE),
  });

  const applyPendingDragPosition = () => {
    dragRafRef.current = null;
    const pending = pendingDragPosRef.current;
    const target = dragTargetRef.current;
    if (!pending || !target) return;

    // Snap the item center to the canvas center lines
    let { x, y } = pending;
    let v = false;
    let h = false;
    if (Math.abs(x + target.width / 2 - REFERENCE_WIDTH / 2) < SNAP_THRESHOLD) {
      x = (REFERENCE_WIDTH - target.width) / 2;
      v = true;
    }
    if (Math.abs(y + target.height / 2 - referenceHeight() / 2) < SNAP_THRESHOLD) {
      y = (referenceHeight() - target.height) / 2;
      h = true;
    }
    setSnapGuides((prev) => (prev.v === v && prev.h === h ? prev : { v, h }));

    const clamped = clampPosition(x, y, target.width, target.height);
    const moveItem = (items) => items.map(item =>
      item.id === target.id ? { ...item, x: clamped.x, y: clamped.y } : item
    );
    if (target.kind === 'image') {
      setCanvasImages(moveItem);
    } else {
      setCanvasElements(moveItem);
    }
    setHasUnsavedChanges(true);
  };

  const startDrag = (item, clientX, clientY) => {
    // Double-tap/double-click on a text item opens on-canvas editing.
    // Generous 600ms window: selecting the item opens the mobile tool panel,
    // which reflows the canvas mid-double-tap.
    const now = Date.now();
    if (item.type === 'text' && lastTapRef.current.id === item.id && now - lastTapRef.current.time < 600) {
      lastTapRef.current = { id: null, time: 0 };
      setSelectedImage(item.id);
      setEditingTextId(item.id);
      return;
    }
    lastTapRef.current = { id: item.id, time: now };

    const rect = canvasRef.current.getBoundingClientRect();
    setSelectedImage(item.id);
    setIsDragging(true);
    dragTargetRef.current = {
      id: item.id,
      kind: canvasImages.some(img => img.id === item.id) ? 'image' : 'element',
      width: item.width,
      height: item.height,
    };
    // Use scaled item position since we're comparing against screen coordinates
    setDragOffset({
      x: clientX - rect.left - (item.x * scaleFactor),
      y: clientY - rect.top - (item.y * scaleFactor),
    });
    // Auto-open element edit menu on mobile when an item is selected
    setMobileToolTab('element');
  };

  const recordInteractionOnce = () => {
    if (interactionRecordedRef.current) return;
    interactionRecordedRef.current = true;
    recordHistory();
  };

  const queueDragMove = (clientX, clientY) => {
    if (!isDragging || !dragTargetRef.current) return;
    recordInteractionOnce();
    const rect = canvasRef.current.getBoundingClientRect();
    // Calculate screen position then convert to reference coordinates
    pendingDragPosRef.current = {
      x: (clientX - rect.left - dragOffset.x) / scaleFactor,
      y: (clientY - rect.top - dragOffset.y) / scaleFactor,
    };
    if (dragRafRef.current === null) {
      dragRafRef.current = requestAnimationFrame(applyPendingDragPosition);
    }
  };

  // Scale (and optionally rotate) an item around its top-left corner.
  // Shared by the two-finger gesture and the corner resize handle.
  const applyItemScale = (id, kind, base, scale, rotation = null) => {
    const nextRotation = rotation === null ? undefined : rotation;
    const transform = (items) => items.map((item) => {
      if (item.id !== id) return item;
      const next = { ...item };
      if (nextRotation !== undefined) next.rotation = nextRotation;
      if (kind === 'image') {
        next.width = Math.max(50, base.width * scale);
        next.height = Math.max(50, base.height * scale);
      } else {
        next.width = Math.max(20, (base.width || 60) * scale);
        next.height = Math.max(20, (base.height || 60) * scale);
        if (item.type === 'text') {
          next.fontSize = Math.max(12, (base.fontSize || 24) * scale);
        } else {
          next.size = Math.max(20, (base.size || 48) * scale);
        }
      }
      return next;
    });
    if (kind === 'image') {
      setCanvasImages(transform);
    } else {
      setCanvasElements(transform);
    }
    setHasUnsavedChanges(true);
  };

  // --- Two-finger pinch-to-resize / rotate on the selected item ---

  const getTouchGeometry = (touches) => {
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return { dist: Math.hypot(dx, dy), angle: (Math.atan2(dy, dx) * 180) / Math.PI };
  };

  const applyPendingGesture = () => {
    gestureRafRef.current = null;
    const gesture = gestureRef.current;
    const geo = pendingGestureRef.current;
    if (!gesture || !geo) return;
    const scale = geo.dist / gesture.startDist;
    const rotation = Math.round(gesture.base.rotation + (geo.angle - gesture.startAngle)) % 360;
    applyItemScale(gesture.id, gesture.kind, gesture.base, scale, rotation);
  };

  const handleCanvasTouchStart = (e) => {
    if (e.touches.length !== 2 || !selectedImage) return;
    const item = canvasImages.find(i => i.id === selectedImage)
      || canvasElements.find(i => i.id === selectedImage);
    if (!item) return;
    recordInteractionOnce();
    const geo = getTouchGeometry(e.touches);
    gestureRef.current = {
      id: item.id,
      kind: canvasImages.some(i => i.id === item.id) ? 'image' : 'element',
      startDist: geo.dist,
      startAngle: geo.angle,
      base: { ...item },
    };
    // A pinch supersedes any in-progress one-finger drag
    dragTargetRef.current = null;
    pendingDragPosRef.current = null;
    setIsDragging(false);
  };

  // --- Corner resize handle (mouse + touch) ---

  const startHandleResize = (item, clientX, clientY) => {
    recordInteractionOnce();
    const rect = canvasRef.current.getBoundingClientRect();
    const originX = rect.left + item.x * scaleFactor;
    const originY = rect.top + item.y * scaleFactor;
    resizeSessionRef.current = {
      id: item.id,
      kind: canvasImages.some(i => i.id === item.id) ? 'image' : 'element',
      base: { ...item },
      originX,
      originY,
      startDist: Math.max(20, Math.hypot(clientX - originX, clientY - originY)),
    };
  };

  const applyPendingResize = () => {
    resizeRafRef.current = null;
    const session = resizeSessionRef.current;
    const point = pendingResizeRef.current;
    if (!session || !point) return;
    const dist = Math.hypot(point.x - session.originX, point.y - session.originY);
    applyItemScale(session.id, session.kind, session.base, dist / session.startDist);
  };

  const queueHandleResize = (clientX, clientY) => {
    pendingResizeRef.current = { x: clientX, y: clientY };
    if (resizeRafRef.current === null) {
      resizeRafRef.current = requestAnimationFrame(applyPendingResize);
    }
  };

  const endDrag = () => {
    // Flush the last queued position so the item doesn't jump back
    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      applyPendingDragPosition();
    }
    dragTargetRef.current = null;
    pendingDragPosRef.current = null;
    resizeSessionRef.current = null;
    pendingResizeRef.current = null;
    interactionRecordedRef.current = false;
    setIsDragging(false);
    setSnapGuides((prev) => (prev.v || prev.h ? { v: false, h: false } : prev));
  };

  const handleMouseDown = (e, item) => startDrag(item, e.clientX, e.clientY);
  const handleMouseMove = (e) => {
    if (resizeSessionRef.current) {
      queueHandleResize(e.clientX, e.clientY);
      return;
    }
    queueDragMove(e.clientX, e.clientY);
  };
  const handleTouchStart = (e, item) => {
    if (e.touches.length > 1) return; // second finger is handled by the canvas gesture
    const touch = e.touches[0];
    startDrag(item, touch.clientX, touch.clientY);
  };
  const handleTouchMove = (e) => {
    if (gestureRef.current && e.touches.length >= 2) {
      pendingGestureRef.current = getTouchGeometry(e.touches);
      if (gestureRafRef.current === null) {
        gestureRafRef.current = requestAnimationFrame(applyPendingGesture);
      }
      return;
    }
    const touch = e.touches[0];
    if (!touch) return;
    if (resizeSessionRef.current) {
      queueHandleResize(touch.clientX, touch.clientY);
      return;
    }
    queueDragMove(touch.clientX, touch.clientY);
  };
  const handleTouchEnd = (e) => {
    if (gestureRef.current && e.touches.length < 2) {
      // Flush the last queued gesture frame so the item keeps its final size
      if (gestureRafRef.current !== null) {
        cancelAnimationFrame(gestureRafRef.current);
        gestureRafRef.current = null;
        applyPendingGesture();
      }
      gestureRef.current = null;
      pendingGestureRef.current = null;
    }
    if (e.touches.length === 0) {
      endDrag();
    }
  };

  // --- Item manipulation ---

  const requestDeleteSelected = () => {
    if (!selectedImage) return;
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSelected = () => {
    recordHistory();
    setCanvasImages(prev => prev.filter(img => img.id !== selectedImage));
    setCanvasElements(prev => prev.filter(el => el.id !== selectedImage));
    setSelectedImage(null);
    setShowDeleteConfirm(false);
    setHasUnsavedChanges(true);
  };

  const rotateImage = () => {
    if (!selectedImage) return;
    recordHistory();
    setCanvasImages(canvasImages.map(img =>
      img.id === selectedImage ? { ...img, rotation: (img.rotation + 15) % 360 } : img
    ));
    setCanvasElements(canvasElements.map(el =>
      el.id === selectedImage ? { ...el, rotation: (el.rotation + 15) % 360 } : el
    ));
    setHasUnsavedChanges(true);
  };

  const bringToFront = () => {
    if (!selectedImage) return;
    recordHistory();
    const allItems = [...canvasImages, ...canvasElements];
    const maxZ = Math.max(...allItems.map(item => item.zIndex), 0);
    setCanvasImages(canvasImages.map(img =>
      img.id === selectedImage ? { ...img, zIndex: maxZ + 1 } : img
    ));
    setCanvasElements(canvasElements.map(el =>
      el.id === selectedImage ? { ...el, zIndex: maxZ + 1 } : el
    ));
    setHasUnsavedChanges(true);
  };

  const sendToBack = () => {
    if (!selectedImage) return;
    recordHistory();
    const allItems = [...canvasImages, ...canvasElements];
    const minZ = Math.min(...allItems.map(item => item.zIndex), 0);
    setCanvasImages(canvasImages.map(img =>
      img.id === selectedImage ? { ...img, zIndex: minZ - 1 } : img
    ));
    setCanvasElements(canvasElements.map(el =>
      el.id === selectedImage ? { ...el, zIndex: minZ - 1 } : el
    ));
    setHasUnsavedChanges(true);
  };

  const resizeImage = (scale) => {
    if (!selectedImage) return;
    recordHistory();
    setCanvasImages(canvasImages.map(img => {
      if (img.id === selectedImage) {
        return {
          ...img,
          width: Math.max(50, img.width * scale),
          height: Math.max(50, img.height * scale)
        };
      }
      return img;
    }));
    setCanvasElements(canvasElements.map(el => {
      if (el.id === selectedImage) {
        return {
          ...el,
          fontSize: el.type === 'text' ? Math.max(12, (el.fontSize || 24) * scale) : el.fontSize,
          size: el.type === 'emoji' ? Math.max(20, (el.size || 48) * scale) : el.size
        };
      }
      return el;
    }));
    setHasUnsavedChanges(true);
  };

  const nudgeSelected = (dx, dy) => {
    if (!selectedImage) return;
    // Coalesce rapid arrow-key presses into one history entry
    const now = Date.now();
    if (now - lastNudgeRecordRef.current > 500) {
      recordHistory();
    }
    lastNudgeRecordRef.current = now;
    const move = (items) => items.map(item => {
      if (item.id !== selectedImage) return item;
      const clamped = clampPosition(item.x + dx, item.y + dy, item.width, item.height);
      return { ...item, x: clamped.x, y: clamped.y };
    });
    setCanvasImages(move);
    setCanvasElements(move);
    setHasUnsavedChanges(true);
  };

  // Keyboard access for the canvas: arrows nudge, Delete removes, Escape deselects
  const handleCanvasKeyDown = (e) => {
    if (!selectedImage) return;
    const step = e.shiftKey ? 1 : 10;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        nudgeSelected(-step, 0);
        break;
      case 'ArrowRight':
        e.preventDefault();
        nudgeSelected(step, 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        nudgeSelected(0, -step);
        break;
      case 'ArrowDown':
        e.preventDefault();
        nudgeSelected(0, step);
        break;
      case 'Delete':
      case 'Backspace':
        e.preventDefault();
        requestDeleteSelected();
        break;
      case 'Escape':
        setSelectedImage(null);
        break;
      default:
        break;
    }
  };

  const commitTitle = (raw) => {
    setEditingTitle(false);
    const title = raw.trim();
    if (!title || title === board?.title) return;
    setBoards(prev => ({
      ...prev,
      [boardId]: { ...prev[boardId], title }
    }));
    setHasUnsavedChanges(true);
  };

  const updateBackground = (preset) => {
    recordHistory();
    setBackground(preset);
    setHasUnsavedChanges(true);
  };

  const updateCanvasSize = (size) => {
    recordHistory();
    setCanvasSize(size);
    setHasUnsavedChanges(true);
  };

  const updateFrame = (frameStyle) => {
    recordHistory();
    setCanvasFrame(frameStyle);
    setHasUnsavedChanges(true);
  };

  const addEmoji = (emoji) => {
    recordHistory();
    const newEmoji = {
      id: `emoji_${Date.now()}`,
      type: 'emoji',
      content: emoji,
      x: 200,
      y: 200,
      size: 48,
      rotation: 0,
      zIndex: Math.max(...canvasImages.map(img => img.zIndex), ...canvasElements.map(el => el.zIndex), 0) + 1,
    };
    setCanvasElements([...canvasElements, newEmoji]);
    setHasUnsavedChanges(true);
  };

  const addText = () => {
    recordHistory();
    // Get canvas dimensions
    const canvasWidth = canvasRef.current?.offsetWidth || REFERENCE_WIDTH;
    const canvasHeight = canvasRef.current?.offsetHeight || 1131;

    // Create a temporary canvas to measure the text
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    const fontSize = 48;
    tempCtx.font = `normal normal ${fontSize}px Arial`;
    const textMetrics = tempCtx.measureText('New Text');

    const newTextElement = {
      id: `text_${Date.now()}`,
      type: 'text',
      content: 'New Text',
      x: (canvasWidth - textMetrics.width) / 2, // Center horizontally
      y: canvasHeight / 2, // Center vertically
      width: textMetrics.width,
      height: fontSize, // Approximate height
      fontSize: fontSize,
      fontWeight: 'normal',
      fontStyle: 'normal',
      color: '#000000',
      rotation: 0,
      zIndex: Math.max(...canvasImages.map(img => img.zIndex), ...canvasElements.map(el => el.zIndex), 0) + 1,
    };
    setCanvasElements([...canvasElements, newTextElement]);
    setSelectedImage(newTextElement.id);
    setHasUnsavedChanges(true);
  };

  const updateTextContent = (newContent) => {
    if (!selectedImage) return;

    // Find the current element to get its fontSize
    const currentElement = canvasElements.find(el => el.id === selectedImage && el.type === 'text');
    if (!currentElement) return;

    // Measure new text dimensions
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    const fontSize = currentElement.fontSize || 48;
    tempCtx.font = `${currentElement.fontWeight || 'normal'} ${fontSize}px Arial`;

    // Handle multiline text - find widest line
    const lines = newContent.split('\n');
    let maxWidth = 0;
    for (const line of lines) {
      const lineWidth = tempCtx.measureText(line || ' ').width;
      if (lineWidth > maxWidth) maxWidth = lineWidth;
    }

    // Calculate height based on number of lines
    const lineHeight = fontSize * 1.2;
    const totalHeight = Math.max(lines.length, 1) * lineHeight;

    setCanvasElements(canvasElements.map(el =>
      el.id === selectedImage && el.type === 'text'
        ? { ...el, content: newContent, width: Math.max(maxWidth, 50), height: totalHeight }
        : el
    ));
    setHasUnsavedChanges(true);
  };

  const updateTextColor = (newColor) => {
    if (!selectedImage) return;
    setCanvasElements(canvasElements.map(el =>
      el.id === selectedImage && el.type === 'text' ? { ...el, color: newColor } : el
    ));
    setHasUnsavedChanges(true);
  };

  const handleSaveBoard = async () => {
    // Get the actual canvas width for PDF generation
    const canvasWidth = canvasRef.current?.offsetWidth || REFERENCE_WIDTH;

    const boardData = {
      id: boardId,
      title: board?.title || t('editor.defaultBoardTitle', { date: new Date().toLocaleDateString() }),
      data: JSON.stringify({
        visions: board?.parsedData?.visions || [],
        canvasImages,
        canvasElements,
        background,
        canvasFrame,
        canvasSize,
        canvasWidth, // Save the actual canvas width for PDF generation
      })
    };

    if (!token) {
      showToastMessage(t('editor.loginToSave'));
      return;
    }

    try {
      await api.saveBoard(token, boardData);
      showToastMessage(t('editor.savedSuccessfully'));
      setHasUnsavedChanges(false);
      // Update local state
      setBoards({
        ...boards,
        [boardId]: {
          ...board,
          parsedData: {
            ...board.parsedData,
            canvasImages,
            canvasElements,
            background,
            canvasFrame,
            canvasSize
          }
        }
      });

      // Update iOS widget with this board's images for rotation
      if (isNativeApp() && canvasImages.length > 0) {
        const imageUrls = canvasImages
          .filter(img => img.imageUrl)
          .map(img => img.imageUrl)
          .slice(0, 8);

        NativeBridge.updateWidget({
          imageUrl: imageUrls[0],     // Legacy single image
          imageUrls: imageUrls,       // Multiple images for rotation
          title: board?.title || t('dashboard.defaultBoardTitle'),
          affirmation: t('dashboard.defaultAffirmation'),
          boardId: boardId
        });
      }
    } catch (e) {
      console.error('Error saving board:', e);
      showToastMessage(t('editor.saveFailed'));
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    recordHistory();

    for (let idx = 0; idx < files.length; idx++) {
      const file = files[idx];

      try {
        // Upload to storage first
        const uploadResult = await api.uploadImage(token, file);

        // Load image to get dimensions
        const img = new Image();
        img.onload = () => {
          // Calculate dimensions while maintaining aspect ratio
          const aspectRatio = img.width / img.height;
          const maxSize = 300;

          let width, height;
          if (img.width > img.height) {
            // Landscape orientation
            width = Math.min(maxSize, img.width);
            height = width / aspectRatio;
          } else {
            // Portrait orientation
            height = Math.min(maxSize, img.height);
            width = height * aspectRatio;
          }

          const newImage = {
            id: `img_${Date.now()}_${idx}`,
            imageUrl: uploadResult.imageUrl,
            filename: uploadResult.filename,
            expiresAt: uploadResult.expiresAt,
            x: 100 + idx * 50,
            y: 100 + idx * 50,
            width: width,
            height: height,
            rotation: 0,
            zIndex: canvasImages.length + canvasElements.length + idx,
          };
          setCanvasImages(prev => [...prev, newImage]);
          setHasUnsavedChanges(true);
        };
        img.src = uploadResult.imageUrl;
      } catch (error) {
        console.error('Error uploading image:', error);
        showToastMessage(t('editor.uploadFailed'));
      }
    }

    // Clear the file input
    e.target.value = '';
  };

  const selected = canvasImages.find(img => img.id === selectedImage) || canvasElements.find(el => el.id === selectedImage);

  // Background presets
  const backgroundPresets = [
    { type: 'gradient', value: 'from-gray-50 to-gray-100', label: 'Light Gray' },
    { type: 'gradient', value: 'from-purple-50 to-pink-50', label: 'Purple Pink' },
    { type: 'gradient', value: 'from-blue-50 to-cyan-50', label: 'Blue Cyan' },
    { type: 'gradient', value: 'from-green-50 to-emerald-50', label: 'Green' },
    { type: 'gradient', value: 'from-yellow-50 to-orange-50', label: 'Warm' },
    { type: 'gradient', value: 'from-rose-50 to-pink-50', label: 'Rose' },
    { type: 'solid', value: 'bg-white', label: 'White' },
    { type: 'solid', value: 'bg-black', label: 'Black' },
    { type: 'pattern', value: 'dots', label: 'Dots' },
    { type: 'pattern', value: 'grid', label: 'Grid' },
  ];

  const commonEmojis = ['❤️', '⭐', '✨', '🌟', '💫', '🌈', '🦋', '🌸', '🌺', '🌻', '🌼', '🌷', '🏠', '🏖️', '✈️', '🎯', '💰', '💎', '👑', '🏆', '🎓', '📚', '💪', '🧘', '🏃', '🎨', '🎵', '☕', '🍷', '🎂'];

  const frameStyles = [
    { key: 'classic', border: '4px solid #8B4513', shadow: '0 4px 6px rgba(0,0,0,0.1)' },
    { key: 'modern', border: '2px solid #333', shadow: '0 8px 16px rgba(0,0,0,0.15)' },
    { key: 'gold', border: '6px solid #FFD700', shadow: '0 4px 12px rgba(255,215,0,0.3)' },
    { key: 'silver', border: '5px solid #C0C0C0', shadow: '0 4px 10px rgba(192,192,192,0.3)' },
    { key: 'vintage', border: '8px double #8B4513', shadow: '0 6px 12px rgba(0,0,0,0.2)' },
    { key: 'polaroid', border: '16px solid #fff', shadow: '0 8px 20px rgba(0,0,0,0.25)', bottomBorder: '48px' },
  ];

  const canvasSizes = [
    { key: 'a4', width: 210, height: 297, unit: 'mm', aspectRatio: 297/210 },
    { key: 'a4Landscape', width: 297, height: 210, unit: 'mm', aspectRatio: 210/297 },
    { key: 'letter', width: 8.5, height: 11, unit: 'in', aspectRatio: 11/8.5 },
    { key: 'letterLandscape', width: 11, height: 8.5, unit: 'in', aspectRatio: 8.5/11 },
    { key: 'a3', width: 297, height: 420, unit: 'mm', aspectRatio: 420/297 },
    { key: 'a3Landscape', width: 420, height: 297, unit: 'mm', aspectRatio: 297/420 },
    { key: 'square', width: 210, height: 210, unit: 'mm', aspectRatio: 1 },
    { key: 'ratio169', width: 16, height: 9, unit: 'ratio', aspectRatio: 9/16 },
    { key: 'ratio43', width: 4, height: 3, unit: 'ratio', aspectRatio: 3/4 },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (loadError || !board) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col items-center justify-center gap-4 p-4">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <p className="text-gray-700 text-center max-w-sm">{t('editor.loadFailed')}</p>
        <div className="flex gap-3">
          <button
            onClick={loadBoard}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition"
          >
            {t('common.retry')}
          </button>
          <button
            onClick={onBackToDashboard}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition"
          >
            {t('editor.backToBoards')}
          </button>
        </div>
      </div>
    );
  }

  const unsavedIndicator = hasUnsavedChanges && (
    <>
      <span className="w-2 h-2 rounded-full bg-amber-300" aria-hidden="true" />
      <span className="sr-only">{t('editor.unsavedChanges')}</span>
    </>
  );

  return (
    <div className="h-screen-safe overflow-hidden bg-gray-100 flex flex-col">
      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}

      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBackToDashboard}
              aria-label={t('editor.backToBoards')}
              className="lg:hidden flex-shrink-0 flex items-center justify-center w-11 h-11 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            {editingTitle ? (
              <input
                autoFocus
                defaultValue={board.title || ''}
                aria-label={t('editor.renameBoardAria')}
                placeholder={t('editor.visionBoard')}
                onBlur={(e) => commitTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                className="text-xl lg:text-3xl font-bold text-gray-900 bg-transparent border-b-2 border-purple-400 outline-none min-w-0 w-full"
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingTitle(true)}
                aria-label={t('editor.renameBoardAria')}
                className="flex items-center gap-2 min-w-0 group"
              >
                <h2 className="text-xl lg:text-3xl font-bold text-gray-900 truncate">{board.title || t('editor.visionBoard')}</h2>
                <Pencil className="w-4 h-4 text-gray-400 group-hover:text-purple-500 flex-shrink-0" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Desktop Toolbar — one brand-gradient primary (Save), quiet neutrals for the rest */}
          <div className="hidden lg:flex gap-3">
            <button
              onClick={onBackToDashboard}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              {t('editor.backToBoards')}
            </button>
            <button
              onClick={undo}
              disabled={!canUndo}
              aria-label={t('editor.undo')}
              title={t('editor.undo')}
              className="flex items-center justify-center w-11 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Undo2 className="w-5 h-5" />
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              aria-label={t('editor.redo')}
              title={t('editor.redo')}
              className="flex items-center justify-center w-11 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Redo2 className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onAddVision}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition"
            >
              <Sparkles className="w-5 h-5" />
              {t('editor.addVision')}
            </button>
            <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition cursor-pointer">
              <ImageIcon className="w-5 h-5" />
              {t('editor.uploadImage')}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
            {/* A purchased PDF is digital content, so on iOS it would have to
                go through In-App Purchase (guideline 3.1.1). Physical prints
                below are exempt and keep the external checkout. */}
            {canPurchaseDigitalGoods() && (
              <button
                onClick={() => handleOrderPrint(boardId, 'pdf')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                <Download className="w-5 h-5" />
                {t('editor.pdfPrice')}
              </button>
            )}
            <button
              onClick={() => setShowPrintModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              <Package className="w-5 h-5" />
              {t('editor.printPrice')}
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              <Share2 className="w-5 h-5" />
              {t('share.shareBoard')}
            </button>
            <button
              onClick={handleSaveBoard}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition font-medium"
            >
              <Save className="w-5 h-5" />
              {t('editor.saveBoard')}
              {unsavedIndicator}
            </button>
          </div>

          {/* Mobile Toolbar */}
          <div className="flex lg:hidden items-center gap-2 flex-shrink-0">
            <button
              onClick={undo}
              disabled={!canUndo}
              aria-label={t('editor.undo')}
              className="flex items-center justify-center w-11 h-11 bg-white border border-gray-300 text-gray-700 rounded-lg disabled:opacity-40"
            >
              <Undo2 className="w-5 h-5" />
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              aria-label={t('editor.redo')}
              className="flex items-center justify-center w-11 h-11 bg-white border border-gray-300 text-gray-700 rounded-lg disabled:opacity-40"
            >
              <Redo2 className="w-5 h-5" />
            </button>
            <button
              onClick={handleSaveBoard}
              className="flex items-center justify-center px-4 py-2 min-h-[44px] bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium gap-2"
            >
              <Save className="w-4 h-4" />
              {t('common.save')}
              {unsavedIndicator}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area - fills remaining height */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">

        <PrintModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          shippingAddress={shippingAddress}
          onShippingAddressChange={setShippingAddress}
          onOrderPrint={() => handleOrderPrint(boardId, 'print', shippingAddress)}
          onShowToast={showToastMessage}
        />

        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          isPublic={isPublic}
          shareUrl={shareUrl}
          isSharing={isSharing}
          onShare={async () => {
            setIsSharing(true);
            const result = await shareBoard(boardId);
            setIsSharing(false);
            if (result.success) {
              setShareUrl(result.data.share_url);
              setIsPublic(true);
              showToastMessage(t('share.boardIsPublic'));
            } else {
              showToastMessage(t('share.sharingError'));
            }
          }}
          onUnshare={async () => {
            setIsSharing(true);
            const result = await unshareBoard(boardId);
            setIsSharing(false);
            if (result.success) {
              setIsPublic(false);
              setShareUrl('');
              showToastMessage(t('share.boardIsPrivate'));
              setShowShareModal(false);
            }
          }}
          onShowToast={showToastMessage}
        />

        {showDeleteConfirm && (
          <Modal
            onClose={() => setShowDeleteConfirm(false)}
            labelledBy="delete-confirm-title"
            centered
            panelClassName="p-6"
          >
            <h3 id="delete-confirm-title" className="text-xl font-bold text-gray-900 mb-2">
              {t('editor.confirmDeleteTitle')}
            </h3>
            <p className="text-gray-600 mb-6">{t('editor.confirmDeleteMessage')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDeleteSelected}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
              >
                {t('common.delete')}
              </button>
            </div>
          </Modal>
        )}

        {/* Canvas Area - takes remaining space, centers canvas */}
        <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-4 lg:p-6 bg-gray-200">
          <div
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchStart={handleCanvasTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onKeyDown={handleCanvasKeyDown}
            className={`relative rounded-lg shadow-2xl overflow-hidden cursor-move ${
              background.type === 'gradient' ? `bg-white ${background.value}` :
              background.type === 'solid' ? background.value :
              background.type === 'pattern' && background.value === 'dots' ? 'bg-white' :
              background.type === 'pattern' && background.value === 'grid' ? 'bg-white' :
              'bg-gradient-to-br from-gray-50 to-gray-100'
            }`}
            style={{
              aspectRatio: `${canvasSize.width} / ${canvasSize.height}`,
              height: `${canvasDisplayHeight}px`,
              width: `${canvasDisplayWidth}px`,
              touchAction: 'none', // Prevent page scroll while dragging
              backgroundImage: background.type === 'pattern' && background.value === 'dots'
                ? 'radial-gradient(circle, #ddd 1px, transparent 1px)'
                : background.type === 'pattern' && background.value === 'grid'
                ? 'linear-gradient(#ddd 1px, transparent 1px), linear-gradient(90deg, #ddd 1px, transparent 1px)'
                : undefined,
              backgroundSize: background.type === 'pattern' && background.value === 'dots'
                ? '20px 20px'
                : background.type === 'pattern' && background.value === 'grid'
                ? '20px 20px'
                : undefined,
              border: canvasFrame?.border || undefined,
              boxShadow: canvasFrame?.shadow || undefined,
              borderBottomWidth: canvasFrame?.bottomBorder || undefined,
            }}
          >
              {/* Empty-canvas hint */}
              {canvasImages.length === 0 && canvasElements.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none text-gray-400 p-6 text-center">
                  <Sparkles className="w-8 h-8" aria-hidden="true" />
                  <p className="text-sm max-w-xs">{t('editor.emptyCanvasHint')}</p>
                </div>
              )}

              {/* Center snap guides shown while dragging */}
              {snapGuides.v && (
                <div className="absolute top-0 bottom-0 w-px bg-purple-400 pointer-events-none" style={{ left: '50%', zIndex: 9999 }} aria-hidden="true" />
              )}
              {snapGuides.h && (
                <div className="absolute left-0 right-0 h-px bg-purple-400 pointer-events-none" style={{ top: '50%', zIndex: 9999 }} aria-hidden="true" />
              )}

              {[...canvasImages, ...canvasElements].sort((a, b) => a.zIndex - b.zIndex).map((item) => {
                const displayWidth = item.width * scaleFactor;
                const displayHeight = item.height * scaleFactor;
                // Keep a comfortable (44px) touch target even for tiny items
                const hitPadX = Math.max(0, (44 - displayWidth) / 2);
                const hitPadY = Math.max(0, (44 - displayHeight) / 2);
                const isSelected = selectedImage === item.id;
                const isEditingThisText = editingTextId === item.id && item.type === 'text';

                return (
                <div
                  key={item.id}
                  onMouseDown={(e) => {
                    if (isEditingThisText) {
                      // Don't let the wrapper steal focus from the textarea
                      e.preventDefault();
                      return;
                    }
                    handleMouseDown(e, item);
                  }}
                  onTouchStart={(e) => { if (!isEditingThisText) handleTouchStart(e, item); }}
                  onFocus={() => setSelectedImage(item.id)}
                  tabIndex={0}
                  role="button"
                  aria-label={item.imageUrl ? t('editor.imageItemAria') : item.content}
                  className={`absolute cursor-move transition-shadow focus:outline-none ${
                    isSelected ? 'ring-4 ring-purple-500' : 'focus:ring-2 focus:ring-purple-300'
                  } ${isDragging && isSelected ? 'canvas-dragging' : ''}`}
                  style={{
                    left: item.x * scaleFactor,
                    top: item.y * scaleFactor,
                    width: displayWidth,
                    height: displayHeight,
                    transform: `rotate(${item.rotation}deg)`,
                    zIndex: item.zIndex,
                  }}
                >
                  {/* Invisible padding that enlarges the touch target of small items */}
                  {(hitPadX > 0 || hitPadY > 0) && (
                    <div
                      className="absolute"
                      style={{ left: -hitPadX, top: -hitPadY, right: -hitPadX, bottom: -hitPadY }}
                      aria-hidden="true"
                    />
                  )}

                  {item.imageUrl ? (
                    failedImages.has(item.id) ? (
                      <div
                        className="w-full h-full rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 flex items-center justify-center pointer-events-none"
                        title={t('editor.imageUnavailable')}
                      >
                        <ImageIcon className="w-8 h-8 text-gray-400" aria-hidden="true" />
                      </div>
                    ) : (
                      <img
                        src={item.imageUrl}
                        alt=""
                        className="w-full h-full object-cover rounded-lg shadow-lg pointer-events-none"
                        draggable="false"
                        onLoad={() => {
                          failedRefreshAttemptsRef.current.delete(item.id);
                        }}
                        onError={() => {
                          if (failedRefreshAttemptsRef.current.has(item.id)) {
                            // Refresh already tried and the new URL failed too —
                            // show the styled placeholder instead of a broken glyph
                            setFailedImages(prev => {
                              const next = new Set(prev);
                              next.add(item.id);
                              return next;
                            });
                          } else {
                            failedRefreshAttemptsRef.current.add(item.id);
                            refreshCanvasImageUrl(item);
                          }
                        }}
                      />
                    )
                  ) : item.type === 'emoji' ? (
                    <div
                      className="pointer-events-none select-none"
                      style={{ fontSize: `${(item.size || 40) * scaleFactor}px`, lineHeight: 1 }}
                    >
                      {item.content}
                    </div>
                  ) : item.type === 'text' ? (
                    isEditingThisText ? (
                      <textarea
                        autoFocus
                        value={item.content}
                        aria-label={t('editor.textPlaceholder')}
                        onFocus={recordHistory}
                        onChange={(e) => updateTextContent(e.target.value)}
                        onBlur={() => setEditingTextId(null)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Escape' || (e.key === 'Enter' && !e.shiftKey)) {
                            e.preventDefault();
                            setEditingTextId(null);
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="w-full h-full bg-white/60 resize-none outline-none rounded"
                        style={{
                          fontSize: `${(item.fontSize || 16) * scaleFactor}px`,
                          color: item.color,
                          fontWeight: item.fontWeight,
                          lineHeight: 1.2,
                        }}
                      />
                    ) : (
                      <div
                        className="pointer-events-none select-none whitespace-pre-wrap break-words"
                        style={{
                          fontSize: `${(item.fontSize || 16) * scaleFactor}px`,
                          color: item.color,
                          fontWeight: item.fontWeight,
                          lineHeight: 1.2,
                          textAlign: 'left',
                        }}
                      >
                        {item.content}
                      </div>
                    )
                  ) : null}

                  {/* Corner resize handle on the selected item */}
                  {isSelected && !isEditingThisText && (
                    <div
                      role="button"
                      aria-label={t('editor.resizeHandleAria')}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        startHandleResize(item, e.clientX, e.clientY);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        const touch = e.touches[0];
                        startHandleResize(item, touch.clientX, touch.clientY);
                      }}
                      className="absolute -bottom-5 -right-5 w-11 h-11 flex items-center justify-center cursor-nwse-resize"
                      style={{ touchAction: 'none' }}
                    >
                      <div className="w-5 h-5 rounded-full bg-white border-2 border-purple-500 shadow-md" />
                    </div>
                  )}
                </div>
                );
              })}
          </div>
        </div>

        {/* Mobile Bottom Toolbar - iOS Camera style */}
        <div className="lg:hidden flex-shrink-0 bg-black/95 backdrop-blur-lg">
          {/* Tool Options Area - shows when a tool is selected */}
          {mobileToolTab && (
            <div className="px-2 py-3 border-b border-white/10">
              {/* Selected Element Controls */}
              {mobileToolTab === 'element' && selectedImage && (
                <div className="flex flex-col gap-3">
                  {/* Text editing controls for text elements */}
                  {selected?.type === 'text' && (
                    <div className="flex items-center gap-2 px-2">
                      <textarea
                        value={selected.content}
                        onFocus={recordHistory}
                        onChange={(e) => updateTextContent(e.target.value)}
                        className="flex-1 px-2 py-1 bg-white/10 text-white rounded text-sm border border-white/20"
                        rows="2"
                        placeholder={t('editor.textPlaceholder')}
                        aria-label={t('editor.textPlaceholder')}
                      />
                      <input
                        type="color"
                        value={selected.color}
                        onFocus={recordHistory}
                        onChange={(e) => updateTextColor(e.target.value)}
                        aria-label={t('editor.textColorAria')}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                    </div>
                  )}
                  {/* Manipulation buttons */}
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => resizeImage(0.8)} aria-label={t('editor.smaller')} className="w-11 h-11 flex items-center justify-center bg-white/10 rounded-full text-white">
                      <span className="text-xl" aria-hidden="true">−</span>
                    </button>
                    <button onClick={() => resizeImage(1.2)} aria-label={t('editor.larger')} className="w-11 h-11 flex items-center justify-center bg-white/10 rounded-full text-white">
                      <span className="text-xl" aria-hidden="true">+</span>
                    </button>
                    <button onClick={rotateImage} aria-label={t('editor.rotate')} className="w-11 h-11 flex items-center justify-center bg-white/10 rounded-full text-white">
                      <RotateCw className="w-5 h-5" />
                    </button>
                    <button onClick={bringToFront} aria-label={t('editor.bringFront')} className="w-11 h-11 flex items-center justify-center bg-white/10 rounded-full text-white">
                      <ArrowUp className="w-5 h-5" />
                    </button>
                    <button onClick={sendToBack} aria-label={t('editor.sendBack')} className="w-11 h-11 flex items-center justify-center bg-white/10 rounded-full text-white">
                      <ArrowDown className="w-5 h-5" />
                    </button>
                    <button onClick={requestDeleteSelected} aria-label={t('common.delete')} className="w-11 h-11 flex items-center justify-center bg-red-500/80 rounded-full text-white">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
              {mobileToolTab === 'element' && !selectedImage && (
                <p className="text-white/60 text-center text-sm">{t('editor.selectElementFirst')}</p>
              )}

              {/* Background Options */}
              {mobileToolTab === 'background' && (
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-fade-x">
                  {backgroundPresets.map((bg, idx) => (
                    <button
                      key={idx}
                      onClick={() => updateBackground(bg)}
                      aria-label={bg.label}
                      className={`flex-shrink-0 w-14 h-14 rounded-xl ${
                        bg.type === 'gradient' ? `bg-gradient-to-br ${bg.value}` :
                        bg.type === 'solid' ? bg.value : 'bg-white'
                      } ${
                        background.value === bg.value ? 'ring-2 ring-white ring-offset-2 ring-offset-black' : ''
                      }`}
                      style={{
                        backgroundImage: bg.type === 'pattern' && bg.value === 'dots'
                          ? 'radial-gradient(circle, #888 1px, transparent 1px)'
                          : bg.type === 'pattern' && bg.value === 'grid'
                          ? 'linear-gradient(#888 1px, transparent 1px), linear-gradient(90deg, #888 1px, transparent 1px)'
                          : undefined,
                        backgroundSize: bg.type === 'pattern' ? '8px 8px' : undefined,
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Emoji Options */}
              {mobileToolTab === 'emoji' && (
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-fade-x">
                  {commonEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => { addEmoji(emoji); setMobileToolTab(null); }}
                      className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl text-2xl flex items-center justify-center hover:bg-white/20 transition"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              {/* Text Option */}
              {mobileToolTab === 'text' && (
                <div className="flex justify-center">
                  <button
                    onClick={() => { addText(); setMobileToolTab(null); }}
                    className="px-6 py-3 bg-white/10 rounded-xl text-white font-medium hover:bg-white/20 transition flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    {t('editor.addText')}
                  </button>
                </div>
              )}

              {/* Frame Options */}
              {mobileToolTab === 'frame' && (
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-fade-x">
                  <button
                    onClick={() => { updateFrame(null); }}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      !canvasFrame ? 'bg-white text-black' : 'bg-white/10 text-white'
                    }`}
                  >
                    {t('common.none')}
                  </button>
                  {frameStyles.map((frame) => (
                    <button
                      key={frame.key}
                      onClick={() => updateFrame(frame)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition ${
                        canvasFrame?.border === frame.border ? 'bg-white text-black' : 'bg-white/10 text-white'
                      }`}
                      style={{ border: frame.border }}
                    >
                      {t(`editor.frameStyles.${frame.key}`)}
                    </button>
                  ))}
                </div>
              )}

              {/* Canvas Size Options */}
              {mobileToolTab === 'size' && (
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-fade-x">
                  {['a4', 'a4Landscape', 'square', 'letter', 'ratio169', 'ratio43']
                    .map((key) => canvasSizes.find((size) => size.key === key))
                    .map((size) => (
                      <button
                        key={size.key}
                        onClick={() => updateCanvasSize(size)}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition ${
                          canvasSize.key === size.key ? 'bg-white text-black' : 'bg-white/10 text-white'
                        }`}
                      >
                        {t(`editor.canvasSizes.${size.key}`)}
                      </button>
                    ))}
                </div>
              )}

              {/* More Actions */}
              {mobileToolTab === 'actions' && (
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-fade-x">
                  <button
                    onClick={() => { onAddVision(); setMobileToolTab(null); }}
                    className="flex-shrink-0 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white text-sm font-medium flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    {t('editor.addVision')}
                  </button>
                  <label className="flex-shrink-0 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-medium flex items-center gap-2 cursor-pointer">
                    <ImageIcon className="w-4 h-4" />
                    {t('editor.upload')}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => { handleImageUpload(e); setMobileToolTab(null); }}
                      className="hidden"
                    />
                  </label>
                  {canPurchaseDigitalGoods() && (
                    <button
                      onClick={() => { handleOrderPrint(boardId, 'pdf'); setMobileToolTab(null); }}
                      className="flex-shrink-0 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      {t('editor.pdfShort')}
                    </button>
                  )}
                  <button
                    onClick={() => { setShowPrintModal(true); setMobileToolTab(null); }}
                    className="flex-shrink-0 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-medium flex items-center gap-2"
                  >
                    <Package className="w-4 h-4" />
                    {t('editor.printShort')}
                  </button>
                  <button
                    onClick={() => { setShowShareModal(true); setMobileToolTab(null); }}
                    className="flex-shrink-0 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-medium flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    {t('share.shareBoard')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tool Category Icons */}
          <div className="flex items-center justify-around px-2 py-3 safe-area-bottom">
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'element' ? null : 'element')}
              aria-pressed={mobileToolTab === 'element'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'element' ? 'text-yellow-400' : 'text-white/70'
              } ${selectedImage ? '' : 'opacity-50'}`}
            >
              <CheckCircle className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.edit')}</span>
            </button>
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'background' ? null : 'background')}
              aria-pressed={mobileToolTab === 'background'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'background' ? 'text-yellow-400' : 'text-white/70'
              }`}
            >
              <Palette className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.color')}</span>
            </button>
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'emoji' ? null : 'emoji')}
              aria-pressed={mobileToolTab === 'emoji'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'emoji' ? 'text-yellow-400' : 'text-white/70'
              }`}
            >
              <Smile className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.emoji')}</span>
            </button>
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'text' ? null : 'text')}
              aria-pressed={mobileToolTab === 'text'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'text' ? 'text-yellow-400' : 'text-white/70'
              }`}
            >
              <Type className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.text')}</span>
            </button>
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'frame' ? null : 'frame')}
              aria-pressed={mobileToolTab === 'frame'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'frame' ? 'text-yellow-400' : 'text-white/70'
              }`}
            >
              <Frame className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.frame')}</span>
            </button>
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'size' ? null : 'size')}
              aria-pressed={mobileToolTab === 'size'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'size' ? 'text-yellow-400' : 'text-white/70'
              }`}
            >
              <Maximize2 className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.size')}</span>
            </button>
            <button
              onClick={() => setMobileToolTab(mobileToolTab === 'actions' ? null : 'actions')}
              aria-pressed={mobileToolTab === 'actions'}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition ${
                mobileToolTab === 'actions' ? 'text-yellow-400' : 'text-white/70'
              }`}
            >
              <Menu className="w-6 h-6" />
              <span className="text-[10px] font-medium">{t('editor.mobileMenu.more')}</span>
            </button>
          </div>
        </div>

        {/* Tools Panel - scrollable sidebar (Desktop only) */}
        <div className="hidden lg:block w-72 flex-shrink-0 bg-gray-50 border-l border-gray-200 overflow-y-auto p-4 space-y-4">

            {selected && (
              <div className="bg-white rounded-xl shadow-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('editor.properties')}</h3>
                <div className="space-y-3">
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>{t('editor.position')}: {Math.round(selected.x)}, {Math.round(selected.y)}</div>
                    <div>{t('editor.size')}: {Math.round(selected.width)} × {Math.round(selected.height)}</div>
                    <div>{t('editor.rotation')}: {selected.rotation}°</div>
                    <div>{t('editor.zIndex')}: {selected.zIndex}</div>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">{t('editor.resize')}</p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => resizeImage(0.8)}
                        className="px-2 py-2 bg-indigo-50 text-indigo-600 rounded text-xs font-medium hover:bg-indigo-100 transition"
                      >
                        − {t('editor.smaller')}
                      </button>
                      <button
                        onClick={() => resizeImage(1)}
                        className="px-2 py-2 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200 transition"
                      >
                        {t('common.reset')}
                      </button>
                      <button
                        onClick={() => resizeImage(1.2)}
                        className="px-2 py-2 bg-indigo-50 text-indigo-600 rounded text-xs font-medium hover:bg-indigo-100 transition"
                      >
                        + {t('editor.larger')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('editor.controls')}</h3>
              <div className="space-y-2">
                <button
                  onClick={requestDeleteSelected}
                  disabled={!selectedImage}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('common.delete')}
                </button>
                <button
                  onClick={rotateImage}
                  disabled={!selectedImage}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCw className="w-4 h-4" />
                  {t('editor.rotate')}
                </button>
                <button
                  onClick={bringToFront}
                  disabled={!selectedImage}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowUp className="w-4 h-4" />
                  {t('editor.bringFront')}
                </button>
                <button
                  onClick={sendToBack}
                  disabled={!selectedImage}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDown className="w-4 h-4" />
                  {t('editor.sendBack')}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                {t('editor.background')}
              </h3>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {backgroundPresets.map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => updateBackground(preset)}
                    aria-label={preset.label}
                    className={`h-8 rounded border-2 transition ${
                      background.value === preset.value ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'
                    } ${
                      preset.type === 'gradient' ? `bg-gradient-to-br ${preset.value}` :
                      preset.type === 'solid' ? preset.value : 'bg-white'
                    }`}
                    style={{
                      backgroundImage: preset.type === 'pattern' && preset.value === 'dots'
                        ? 'radial-gradient(circle, #888 1px, transparent 1px)'
                        : preset.type === 'pattern' && preset.value === 'grid'
                        ? 'linear-gradient(#888 1px, transparent 1px), linear-gradient(90deg, #888 1px, transparent 1px)'
                        : undefined,
                      backgroundSize: preset.type === 'pattern' ? '10px 10px' : undefined,
                    }}
                    title={preset.label}
                  />
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Smile className="w-4 h-4" />
                {t('editor.emojis')}
              </h3>
              <div className="grid grid-cols-5 gap-1 mb-2 max-h-32 overflow-y-auto">
                {commonEmojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => addEmoji(emoji)}
                    className="text-2xl hover:bg-gray-100 rounded p-1 transition"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Type className="w-4 h-4" />
                {t('editor.text')}
              </h3>
              <button
                onClick={addText}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition"
              >
                <Plus className="w-4 h-4" />
                {t('editor.addText')}
              </button>
              {selected && selected.type === 'text' && (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={selected.content}
                    onFocus={recordHistory}
                    onChange={(e) => updateTextContent(e.target.value)}
                    placeholder={t('editor.textPlaceholder')}
                    aria-label={t('editor.textPlaceholder')}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    rows="2"
                  />
                  <input
                    type="color"
                    value={selected.color}
                    onFocus={recordHistory}
                    onChange={(e) => updateTextColor(e.target.value)}
                    aria-label={t('editor.textColorAria')}
                    className="w-full h-8"
                  />
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Frame className="w-4 h-4" />
                {t('editor.frames')}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {frameStyles.map((frame) => (
                  <button
                    key={frame.key}
                    onClick={() => updateFrame(frame)}
                    className="px-3 py-2 text-xs font-medium rounded-lg hover:bg-purple-50 transition border-2"
                    style={{
                      border: frame.border,
                      boxShadow: frame.shadow,
                    }}
                  >
                    {t(`editor.frameStyles.${frame.key}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Maximize2 className="w-4 h-4" />
                {t('editor.canvasSize')}
              </h3>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {canvasSizes.map((size) => (
                  <button
                    key={size.key}
                    onClick={() => updateCanvasSize(size)}
                    className={`px-2 py-1.5 text-xs rounded border-2 transition ${
                      canvasSize.key === size.key ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium' : 'border-gray-200 hover:border-purple-300'
                    }`}
                    title={`${size.width} × ${size.height} ${size.unit}`}
                  >
                    {t(`editor.canvasSizes.${size.key}`)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('editor.current')}: {canvasSize.width} × {canvasSize.height} {canvasSize.unit}
              </p>
            </div>

        </div>
      </div>
    </div>
  );
};

// Root App
const App = () => {
  return (
    <AuthProvider>
      <OfflineBanner />
      <VisionBoardApp />
    </AuthProvider>
  );
};

export default App;
